import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { SECTOR_NAME } from "@tootsweet/model/enums/SectorName";
import { POSA_FORMAT } from "@tootsweet/model/lottery/POSAType";
import KC_Heading2 from "../../../../../designsystem/typography/KC-Heading/KC_Heading2";
import { KC_Weights } from "../../../../../designsystem/ds_commons";
import { tt } from "../../../../../utils/i18n/i18n";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../../../designsystem/KC_Button/KC_Button";
import { RootStateI } from "../../../../../redux";
import Separator from "../../../../../components/Separator";
import POSACarousel from "./POSACarousel";
import "../../../../../styles/fonts.scss";
import "../styles.scss";
import KC_Padder from "../../../../../designsystem/KC_Padder/KC_Padder";
import KC_UIText from "../../../../../designsystem/typography/KC_UIText/KC_UIText";
import { posaDistributionQuantity } from "../../../../../utils/routes/routes";
import { POSA_PLACEHOLDER_IMAGE } from "../../../../17_JetpackOnboarding/constants";
import { BASE_URL } from "../../../../../utils/constants";
import { buildPathWithTrackingId } from "../../../../../utils/routing/routingUtils";
import { useAccountToDisplaySelector } from "../../../../../redux/actions/user";
import POSAForm from "./POSAForm/POSAForm";
import { useSubscriptionPlansSelector } from "../../../../../redux/actions/app";
import { usePOSATypesSelector } from "../../../../../redux/actions/wheel";
import getPOSAListForFormat from "../../../../../utils/helpers/getPOSAListForFormat";

export default function OfflinePOSAGallery() {
  const history = useHistory();
  const currentPOSAOrder = useSelector(
    (state: RootStateI) => state.saas.currentPOSAOrder
  );

  const { accountToDisplay } = useAccountToDisplaySelector();

  const allSubscriptionPlans = useSubscriptionPlansSelector();
  const posaTypes = usePOSATypesSelector();

  const handleNext = () => {
    history.push(
      buildPathWithTrackingId(
        posaDistributionQuantity,
        accountToDisplay?.trackingID
      )
    );
  };

  const hasCustomPOSAFeature = useMemo(() => {
    return (
      !accountToDisplay?.saasFeatures || !!accountToDisplay?.saasFeatures?.cp
    );
  }, [accountToDisplay]);

  const totalQty = useMemo(() => {
    return currentPOSAOrder.items?.length || 0;
  }, [currentPOSAOrder]);

  const PREMIUM_PLANS_IDS = useMemo(() => {
    return (
      allSubscriptionPlans
        ?.filter((p) => p.saasFeatures?.ip || (p.price && p.price > 0))
        ?.map((p) => p._id) ?? []
    );
  }, [allSubscriptionPlans]);

  const FREE_PLANS_IDS = useMemo(() => {
    const ids =
      allSubscriptionPlans?.filter((p) => !p.price).map((p) => p._id) ?? [];
    return ["", null, undefined, ...ids];
  }, [allSubscriptionPlans]);

  const IS_FREE = useMemo(() => {
    return (
      accountToDisplay?.saasPlanId &&
      FREE_PLANS_IDS.includes(accountToDisplay?.saasPlanId)
    );
  }, [accountToDisplay, FREE_PLANS_IDS]);

  const showPOSAForm = useMemo(() => {
    return (
      [SECTOR_NAME.BAR, SECTOR_NAME.CAFE, SECTOR_NAME.RESTAURANT].includes(
        accountToDisplay?.businessUser?.customerType as SECTOR_NAME
      ) && PREMIUM_PLANS_IDS.includes(accountToDisplay?.saasPlanId ?? "")
    );
  }, [
    accountToDisplay?.businessUser?.customerType,
    accountToDisplay?.saasPlan,
    PREMIUM_PLANS_IDS
  ]);

  return (
    <>
      <div className="px-4 mb-4 h-100 d-flex flex-column">
        {showPOSAForm ? (
          <POSAForm />
        ) : (
          POSA_FORMATS.map((posaFormat, idx) => {
            const posaList = getPOSAListForFormat(
              posaFormat.format,
              posaTypes,
              hasCustomPOSAFeature
            );

            if (!posaList.length) {
              return null;
            }

            return (
              <React.Fragment
                key={`${posaFormat.format}-${idx}-posa-offline-item`}
              >
                {idx > 0 && (
                  <>
                    <Separator />
                    <Separator />
                  </>
                )}
                <KC_Heading2 text={posaFormat.name} />
                {!IS_FREE && posaFormat.description && (
                  <KC_UIText
                    className="d-block mb-2 mt-2"
                    text={posaFormat.description}
                    weight="reg"
                  />
                )}
                <POSACarousel
                  format={posaFormat.format}
                  imgURL={`${BASE_URL}${POSA_PLACEHOLDER_IMAGE[posaFormat.format]}`}
                  hidePlaceholderTitle={true}
                  posaList={posaList}
                />
              </React.Fragment>
            );
          })
        )}
      </div>

      <KC_Padder />

      {!!totalQty && (
        <div
          id="posa-footer"
          className="d-flex justify-content-end border-top align-items-center px-4 py-3 mt-3 st-gap offline-posa-footer"
        >
          <KC_UIText
            id="total-plv-qty"
            weight={KC_Weights.md}
            text={`${totalQty} produit${totalQty > 1 ? "s" : ""} sélectionné${
              totalQty > 1 ? "s" : ""
            }`}
          />
          <div>
            <KC_Button
              id="plv-go-to-order-table"
              isPrimary={!!totalQty}
              isTransparent={!totalQty}
              disabled={!totalQty}
              size={KC_ButtonSize.Large}
              text={tt("continue")}
              type={KC_ButtonType.Button}
              onClick={handleNext}
              className="d-flex"
            />
          </div>
        </div>
      )}
    </>
  );
}

const POSA_FORMATS: Array<{
  format: POSA_FORMAT;
  name: string;
  description: string;
}> = [
  {
    format: "businesscardsstandard",
    name: tt("posa_item_business_card"),
    description: tt("posa_format_businesscardsstandard_desc")
  },
  {
    format: "flyer_a5",
    name: tt("posa_flyer_a5"),
    description: tt("posa_format_flyer_a5_desc")
  },
  {
    format: "flyer_a6",
    name: tt("posa_flyer_a6"),
    description: tt("posa_format_flyer_a6_desc")
  },
  {
    format: "chevalet_a5",
    name: tt("posa_item_a5_display"),
    description: tt("posa_format_chevalet_a5_desc")
  },
  {
    format: "chevalet_a6",
    name: tt("posa_item_a6_display"),
    description: tt("posa_format_chevalet_a6_desc")
  },
  {
    format: "stickers10x10",
    name: tt("posa_sticker"),
    description: tt("posa_format_stickers10x10_desc")
  },
  {
    format: "stickers_dome",
    name: tt("posa_sticker_dome"),
    description: tt("posa_format_stickers_dome_desc")
  },
  { format: "poster_a2", name: tt("posa_item_poster_a2"), description: "" },
  { format: "poster_a3", name: tt("posa_item_poster_a3"), description: "" },
  { format: "poster_a4", name: tt("posa_item_poster_a4"), description: "" },
  { format: "kakemono", name: tt("posa_item_kakemono"), description: "" }
];
